import * as React from 'react'
import { cn } from '../../lib/utils'
import { Button } from '../ui/button'
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar'
import { Badge } from '../ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu'
import { Switch } from '../ui/switch'
import { Moon, Sun, User, LogOut } from 'lucide-react'
import { Breadcrumbs, BreadcrumbItem } from './Breadcrumbs'

export interface HeaderUser {
  name?: string
  email?: string
  avatarUrl?: string
}

export interface HeaderLabels {
  user?: string
  profile?: string
  logout?: string
}

export interface HeaderProps {
  user?: HeaderUser
  /** @deprecated Use tenantName instead */
  tenantSlug?: string | null
  tenantName?: string | null
  breadcrumbs?: BreadcrumbItem[]
  onNavigate?: (href: string) => void
  isDarkMode?: boolean
  onToggleDarkMode?: () => void
  onProfileClick?: () => void
  onLogout?: () => void
  sidebarCollapsed?: boolean
  extraActions?: React.ReactNode
  className?: string
  labels?: HeaderLabels
}

export function Header({
  user,
  tenantSlug,
  tenantName,
  breadcrumbs,
  onNavigate,
  isDarkMode = false,
  onToggleDarkMode,
  onProfileClick,
  onLogout,
  sidebarCollapsed = false,
  extraActions,
  className,
  labels,
}: HeaderProps) {
  const getInitials = (name?: string, email?: string) => {
    if (name) {
      return name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2)
    }
    return email?.[0]?.toUpperCase() || 'U'
  }

  const displayName = tenantName || tenantSlug

  return (
    <header
      className={cn(
        'fixed top-0 right-0 z-30 flex h-16 items-center justify-between border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 px-6 transition-all duration-300',
        sidebarCollapsed ? 'left-16' : 'left-64',
        className
      )}
    >
      <div className="flex items-center gap-3 min-w-0">
        {displayName && (
          <Badge
            variant="secondary"
            className="bg-primary/10 text-primary font-semibold flex-shrink-0"
          >
            {displayName}
          </Badge>
        )}
        {displayName && breadcrumbs && breadcrumbs.length > 0 && (
          <div className="h-4 w-px bg-border flex-shrink-0" />
        )}
        {breadcrumbs && breadcrumbs.length > 0 && (
          <Breadcrumbs items={breadcrumbs} onNavigate={onNavigate} />
        )}
      </div>

      <div className="flex items-center gap-4">
        {extraActions}

        {/* Dark Mode Toggle */}
        {onToggleDarkMode && (
          <div className="flex items-center gap-2">
            <Sun className="h-4 w-4 text-muted-foreground" />
            <Switch
              checked={isDarkMode}
              onCheckedChange={onToggleDarkMode}
              className="data-[state=checked]:bg-primary"
            />
            <Moon className="h-4 w-4 text-muted-foreground" />
          </div>
        )}

        {/* User Menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="relative h-9 w-9 rounded-full">
              <Avatar className="h-9 w-9">
                {user?.avatarUrl && <AvatarImage src={user.avatarUrl} alt={user.name} />}
                <AvatarFallback className="bg-gradient-to-br from-primary to-purple-500 text-white font-semibold">
                  {getInitials(user?.name, user?.email)}
                </AvatarFallback>
              </Avatar>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-56" align="end" forceMount>
            <DropdownMenuLabel className="font-normal">
              <div className="flex flex-col space-y-1">
                <p className="text-sm font-medium leading-none">{user?.name || labels?.user || 'User'}</p>
                {user?.email && (
                  <p className="text-xs leading-none text-muted-foreground">
                    {user.email}
                  </p>
                )}
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {onProfileClick && (
              <DropdownMenuItem onClick={onProfileClick}>
                <User className="mr-2 h-4 w-4" />
                <span>{labels?.profile || 'Profile'}</span>
              </DropdownMenuItem>
            )}
            {onLogout && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={onLogout}
                  className="text-destructive focus:text-destructive"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>{labels?.logout || 'Log out'}</span>
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  )
}
